import { AVAILABLE_SUPPORTED_CURRENCIES, DEFAULT_CURRENCY } from '@app/common/constants';

export type SupportedCurrencies = (typeof AVAILABLE_SUPPORTED_CURRENCIES)[keyof typeof AVAILABLE_SUPPORTED_CURRENCIES];

type CurrencyFormatterOptions = {
  hasDecimals?: boolean;
  isCompact?: boolean;
};

export const currencyFormatter = (
  value: number,
  currency: SupportedCurrencies = DEFAULT_CURRENCY,
  locale = 'el-GR',
  options?: CurrencyFormatterOptions
) => {
  const hasDecimals = options?.hasDecimals ?? true;
  const isCompact = options?.isCompact ?? false;

  const numberFormatter = new Intl.NumberFormat(locale, {
    style: 'decimal',
    notation: isCompact ? 'compact' : 'standard',
    compactDisplay: isCompact ? 'short' : undefined,
    minimumFractionDigits: isCompact ? 0 : hasDecimals ? 2 : 0,
    maximumFractionDigits: isCompact ? 1 : hasDecimals ? 2 : 0,
  });

  const formattedNumber = numberFormatter.format(value ?? 0);
  const currencySymbol = getCurrencySymbol(currency, locale);

  return `${currencySymbol}${formattedNumber}`;
};

export const getCurrencySymbol = (currency: SupportedCurrencies = DEFAULT_CURRENCY, locale = 'el-GR') => {
  const parts = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).formatToParts(0);

  const symbolPart = parts.find((p) => p.type === 'currency');
  return symbolPart?.value ?? '';
};

/** A discount percent at the four decimals the backend stores, so equal discounts compare equal. */
export const roundPercent = (value: number) => Math.round(value * 10000) / 10000;

/**
 * A discount percent as a number, up to the four decimals it is stored with, trailing zeros
 * trimmed (8,8771 · 8,88 · 25). Same default locale as `currencyFormatter`; callers add the `%`.
 */
export const formatPercent = (value: number, locale = 'el-GR') =>
  new Intl.NumberFormat(locale, { minimumFractionDigits: 0, maximumFractionDigits: 4, useGrouping: false }).format(
    value ?? 0
  );
